import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Divider, Typography, Image } from 'antd';

import { StyledButton } from '@/lib/theme/components/Button';
import { StyledInput } from '@/lib/theme/components/Input';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import CompanyLogoPremiumIcon from '@/components/Icons/CompanyLogoPremiumIcon';
import { useTrackEvent } from '@/hooks/useTrackEvents';
import { StyledPaymentModal } from '@/components/Shared/StyledPaymentModal';
import { formatCurrencyNumber } from '@/utils/formatCurrencyNumber';
import themeToken from '@lib/theme/tokens/index';

import { PremiumCompanyLogoContainer } from '../../../components/Shared/PremiumCompanyLogoContainer';
import { useSubscribeToPlan } from '../api/subscribeToPlan';
import { useVerifiyPromoCode } from '../api/verifyPromoCode';
import { useVerifyPackagePromoCode } from '../api/verifyPackagePromoCode';
import { usePurchasePackage } from '../api/purchasePackage';

const { Title, Text } = Typography;

const PaymentModal = ({
  isOpen,
  setIsOpen,
  planDetails,
  isPackage,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  planDetails?: { id: number; name: string; price: number };
  isPackage?: boolean;
}) => {
  const { t } = useTranslation('plans');
  const [promoCode, setPromoCode] = useState('');
  const [isPromoCodeVerified, setIsPromoCodeVerified] = useState(false);
  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { sendMixpanelEvent } = useTrackEvent({ page: 'plans' });

  const {
    mutate: verifyPackagePromoCode,
    isPending: isVerifyPackagePromoCodeLoading,
    data: verifyPackagePromoCodeResponse,
  } = useVerifyPackagePromoCode({
    postSuccess: () => {
      sendMixpanelEvent(
        'apply-promocode',
        {},
        {
          status: 'success',
          req_path: `premium/${selectedCompany?.cid as number}/packages/${planDetails?.id}/promo-code/apply`,
        },
      );
      setIsPromoCodeVerified(() => true);
    },
    handleAfterFailure: (code) => {
      sendMixpanelEvent(
        'apply-promocode',
        { statusCode: code },
        {
          status: 'fail',
          req_path: `premium/${selectedCompany?.cid as number}/packages/${planDetails?.id}/promo-code/apply`,
        },
      );
    },
  });

  const handlePaymentModalCancel = () => {
    setIsOpen(false);
  };
  const addPromoCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value);
  };

  const {
    mutate: subscribeToPlan,
    isPending,
    data: subscribeResponse,
  } = useSubscribeToPlan({
    handleAfterFailure: (code) => {
      sendMixpanelEvent(
        'subscribe-to-plan',
        { statusCode: code },
        { status: 'fail', req_path: `/premium/plans/${planDetails?.id}/subscribe` },
      );
    },
  });

  const {
    mutate: purchasePackage,
    isPending: isPurchasePackageLoading,
    data: purchasePackageResponse,
  } = usePurchasePackage({
    handleAfterFailure: (code) => {
      sendMixpanelEvent(
        'subscribe-to-plan',
        { statusCode: code },
        { status: 'fail', req_path: `/premium/${selectedCompany?.cid}/packages/${planDetails?.id}/subscribe` },
      );
    },
  });

  const {
    mutate: verifyPromoCode,
    isPending: isApplyCodeLoading,
    data: verifyCodeResponse,
  } = useVerifiyPromoCode({
    postSuccess: () => {
      sendMixpanelEvent(
        'apply-promocode',
        {},
        { status: 'success', req_path: `/premium/plans/${planDetails?.id}/promo-code/apply` },
      );
      setIsPromoCodeVerified(() => true);
    },
    handleAfterFailure: (code) => {
      sendMixpanelEvent(
        'apply-promocode',
        { statusCode: code },
        { status: 'fail', req_path: `/premium/plans/${planDetails?.id}/promo-code/apply` },
      );
    },
  });

  const subscribe = () => {
    sendMixpanelEvent('subscribe-to-plan-btn-clicked', { code: promoCode, planID: planDetails?.id });
    if (isPackage) {
      purchasePackage({
        packageID: planDetails?.id as number,
        companyID: selectedCompany?.cid as number,
        code: isPromoCodeVerified ? promoCode : '',
      });
    } else {
      subscribeToPlan({
        planID: planDetails?.id as number,
        code: isPromoCodeVerified ? promoCode : '',
      });
    }
  };

  const applyPromoCode = () => {
    sendMixpanelEvent('apply-promocode-btn-clicked', { code: promoCode });
    if (isPackage) {
      verifyPackagePromoCode({
        code: promoCode,
        companyID: selectedCompany?.cid as number,
        packageID: planDetails?.id as number,
      });
    } else {
      verifyPromoCode({
        planID: planDetails?.id as number,
        code: promoCode,
      });
    }
  };

  const resetPromo = () => {
    sendMixpanelEvent('remove-promocode-btn-clicked');
    setPromoCode(() => '');
    setIsPromoCodeVerified(() => false);
  };

  useEffect(() => {
    if (subscribeResponse?.redirect_url) {
      const link = document.createElement('a');
      link.href = subscribeResponse.redirect_url;
      sendMixpanelEvent(
        'subscribe-to-plan',
        {},
        { status: 'success', req_path: `/premium/plans/${planDetails?.id}/subscribe` },
      );
      link.click();
    } else if (purchasePackageResponse?.redirect_url) {
      const link = document.createElement('a');
      link.href = purchasePackageResponse.redirect_url;
      sendMixpanelEvent(
        'purchase-package',
        {},
        { status: 'success', req_path: `/premium/${selectedCompany?.cid}/packages/${planDetails?.id}/subscribe` },
      );
      link.click();
    }
  }, [subscribeResponse, purchasePackageResponse]);

  const getPromoPriceAfterDiscount = () => {
    if (promoCode && isPromoCodeVerified && verifyCodeResponse) {
      return Number(verifyCodeResponse?.price);
    } else if (promoCode && verifyPackagePromoCodeResponse && isPromoCodeVerified) {
      return Number(verifyPackagePromoCodeResponse?.price);
    } else {
      return Number(planDetails?.price);
    }
  };

  return (
    <StyledPaymentModal
      open={isOpen}
      onCancel={handlePaymentModalCancel}
      width={500}
      footer={null}
      centered
      destroyOnClose
    >
      <Title
        style={{
          color: themeToken.colorPrimary,
          fontSize: `${themeToken.fontSizeLG}px`,
          fontWeight: '600',
        }}
      >
        {isPackage ? t('buyPackage') : t('upgradeCompanyProfile')}
      </Title>
      <Text
        style={{
          color: themeToken['branding-natural-6'],
          fontSize: '14px',
          fontWeight: '400',
        }}
      >
        {isPackage ? t('detailsAboutThePackageAndPromocode') : t('detailsAboutThePlanAndPromocode')}
      </Text>
      <Divider
        style={{
          marginBlock: themeToken.margin,
        }}
      />
      <Flex gap={24} vertical>
        <Flex align="center" justify="space-between">
          <Flex gap={8} align="center" justify="space-between">
            <PremiumCompanyLogoContainer>
              <Flex style={{ position: 'absolute', top: '-5px', right: '-5px', zIndex: 10 }}>
                <CompanyLogoPremiumIcon />
              </Flex>
              <Image
                preview={false}
                src={selectedCompany?.logo}
                alt={selectedCompany?.name}
                width={36}
                height={36}
                placeholder
              />
            </PremiumCompanyLogoContainer>
            <Flex vertical>
              <Title
                style={{
                  fontSize: `${themeToken.fontSizeLG}px`,
                  fontWeight: `${themeToken.fontWeightLG}`,
                  color: themeToken['branding-natural-9'],
                }}
              >
                {selectedCompany?.name}
              </Title>
              <Text
                style={{
                  fontSize: `${themeToken.fontSizeLG}px`,
                  fontWeight: `${themeToken.fontWeightNormal}`,
                  color: themeToken['branding-natural-9'],
                }}
              >
                {isPackage ? `${t('buyThePackage')} ${planDetails?.name}` : `${t('upgradeTo')} ${planDetails?.name}`}
              </Text>
            </Flex>
          </Flex>
          <Flex vertical align="end">
            <Text
              style={{
                fontSize: `${themeToken.fontSizeHeading3}px`,
                fontWeight: `${themeToken.fontWeightLG}`,
                color: themeToken['branding-natural-10'],
              }}
            >
              {formatCurrencyNumber(planDetails?.price ?? 0, 'SAR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
            <Text
              style={{
                fontSize: `${themeToken.fontSizeSM}px`,
                fontWeight: `${themeToken.fontWeightNormal}`,
                color: themeToken['branding-natural-6'],
              }}
            >
              {isPackage ? '' : t('paidAnnually')}
            </Text>
          </Flex>
        </Flex>
        <Divider
          style={{
            marginBlock: 0,
          }}
        />
        <Flex gap={4} vertical>
          <Title
            level={5}
            style={{
              fontSize: themeToken.fontSize,
              marginBlockEnd: themeToken.marginXXS,
            }}
          >
            {t('promoCode')}
          </Title>
          <StyledInput
            disabled={isPromoCodeVerified}
            addonAfter={
              <StyledButton
                loading={isApplyCodeLoading || isVerifyPackagePromoCodeLoading}
                type="text"
                size="small"
                onClick={isPromoCodeVerified ? resetPromo : applyPromoCode}
                style={{ background: 'transparent' }}
              >
                {isPromoCodeVerified ? t('reset') : t('apply')}
              </StyledButton>
            }
            placeholder={t('enterPromoCode')}
            size="large"
            value={promoCode}
            onChange={addPromoCode}
          />
        </Flex>
        <Flex gap={12} vertical>
          <Title
            level={5}
            style={{
              fontSize: themeToken.fontSize,
              marginBlockEnd: themeToken.marginXXS,
            }}
          >
            {t('summary')}
          </Title>
          <Flex align="center" justify="space-between">
            <Text
              style={{
                color: themeToken['branding-natural-6'],
              }}
              strong
            >
              {isPackage ? t('package') : t('plan')}
            </Text>
            <Text
              style={{
                fontSize: themeToken.fontSizeLG,
              }}
              strong
            >
              {formatCurrencyNumber(planDetails?.price as number, 'SAR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between">
            <Text
              style={{
                color: themeToken['branding-natural-6'],
              }}
              strong
            >
              {t('discount')}
            </Text>
            <Text
              style={{
                fontSize: themeToken.fontSizeLG,
                color: themeToken['branding-natural-6'],
              }}
              strong
            >
              {formatCurrencyNumber(
                promoCode && isPromoCodeVerified ? (planDetails?.price ?? 0) - (verifyCodeResponse?.price ?? 0) : 0,
                'SAR',
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}
            </Text>
          </Flex>
          <Divider
            style={{
              margin: 0,
            }}
          />
          <Flex align="center" justify="space-between">
            <Text
              style={{
                color: themeToken['branding-natural-6'],
              }}
              strong
            >
              {t('total')}
            </Text>
            <Text
              style={{
                fontSize: themeToken.fontSizeLG,
              }}
              strong
            >
              {formatCurrencyNumber(getPromoPriceAfterDiscount(), 'SAR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <StyledButton
        block
        loading={isPending || isPurchasePackageLoading}
        type="primary"
        style={{
          marginBlockStart: themeToken.margin,
        }}
        onClick={subscribe}
      >
        {t('goToPayment')}
      </StyledButton>
    </StyledPaymentModal>
  );
};

export default PaymentModal;
